import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, BookOpen, FileText, Calendar, Bell, ClipboardList,
  FileCheck, Award, Send, Paperclip, Clock, Check, X, Eye,
  AlertTriangle, ChevronRight, Plus, Filter, Download
} from 'lucide-react';
import { InfoTooltip } from '../../components/ExplanationLayers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * STUDENT NOTEBOOK (BUILD 4A)
 * 
 * Visible from Institution Profile
 * Teacher → Student messaging ONLY
 * Parent read-only visibility
 * ZERO discovery
 * 
 * Attachments:
 * - Homework
 * - Notes
 * - Permission slips
 * - Announcements
 * - Report cards
 */

const ITEM_TYPES = {
  homework: { icon: ClipboardList, color: 'var(--lk-secondary-orange)', label: 'Homework' },
  notes: { icon: FileText, color: 'var(--lk-primary-green)', label: 'Notes' },
  permission_slip: { icon: FileCheck, color: 'var(--lk-earth-brown)', label: 'Permission Slip' },
  announcement: { icon: Bell, color: '#6366f1', label: 'Announcement' },
  report_card: { icon: Award, color: '#ec4899', label: 'Report Card' }
};

export default function StudentNotebook() {
  const navigate = useNavigate();
  const { institutionId, studentId } = useParams();
  const { user } = useContext(AuthContext);
  
  const [notebook, setNotebook] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [userRole, setUserRole] = useState(null); // teacher, parent, student

  useEffect(() => {
    loadNotebook();
  }, [institutionId, studentId]);

  const loadNotebook = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/institutions/${institutionId}/students/${studentId}/notebook`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotebook(response.data.notebook);
      setItems(response.data.items || []);
      setUserRole(response.data.user_role);
    } catch (error) {
      console.error('Failed to load notebook:', error);
      toast.error('Failed to load student notebook');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => 
    filter === 'all' || item.type === filter
  );

  const acknowledgeItem = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/institutions/${institutionId}/students/${studentId}/notebook/${itemId}/acknowledge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(items.map(item => 
        item.item_id === itemId ? { ...item, acknowledged: true, acknowledged_at: new Date().toISOString() } : item
      ));
      toast.success('Acknowledged');
    } catch (error) {
      console.error('Failed to acknowledge:', error);
      toast.error('Failed to acknowledge');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-earth-brown)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center gap-1">
                <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Student Notebook</h1>
                <InfoTooltip topic="student_notebook" />
              </div>
              {notebook?.student_name && (
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{notebook.student_name}</p>
              )}
            </div>
            {userRole === 'teacher' && (
              <Button 
                size="sm"
                onClick={() => navigate(`/institutions/${institutionId}/students/${studentId}/notebook/new`)}
                className="rounded-full"
                style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Parent Visibility Notice */}
      {userRole === 'parent' && (
        <div className="px-4 py-2" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
          <div className="max-w-3xl mx-auto flex items-center gap-2 text-sm" style={{ color: 'var(--lk-primary-green)' }}>
            <Eye className="w-4 h-4" />
            <span>Parent View — You can see all content but cannot send messages</span>
          </div>
        </div>
      )}

      {/* Safety Notice */}
      <div className="px-4 py-3" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-xs flex items-center gap-2" style={{ color: 'var(--fy-text-disabled)' }}>
            <AlertTriangle className="w-3 h-3" />
            This notebook is private. Students cannot message each other. Parents can see all content.
          </p>
        </div>
      </div>

      {/* Child-Friendly Explanation (for students) */}
      {userRole === 'student' && (
        <div className="px-4 py-4" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
          <div className="max-w-3xl mx-auto">
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--lk-primary-green)' }}>
              What is this?
            </h3>
            <p className="text-xs mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
              This is a place your school uses to share important things like school days, events, notes from teachers, and papers your family needs to see.
            </p>
            <p className="text-xs mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
              It is not a game and not social media.
            </p>
            <div className="mt-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(122, 143, 91, 0.1)' }}>
              <p className="text-xs font-medium" style={{ color: 'var(--lk-primary-green)' }}>
                What you can&apos;t do here:
              </p>
              <ul className="text-xs mt-1 space-y-0.5" style={{ color: 'var(--fy-text-secondary)' }}>
                <li>• You can&apos;t look up other students</li>
                <li>• You can&apos;t message students</li>
                <li>• Strangers can&apos;t find or message you</li>
              </ul>
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--fy-text-disabled)' }}>
              If you ever have a question, talk to your parent or teacher.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-4 py-3 overflow-x-auto">
        <div className="max-w-3xl mx-auto flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className="px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap"
            style={{ 
              backgroundColor: filter === 'all' ? 'var(--lk-earth-brown)' : 'var(--fy-surface-muted)',
              color: filter === 'all' ? 'var(--lk-warm-sand)' : 'var(--fy-text-secondary)'
            }}
          >
            All
          </button>
          {Object.entries(ITEM_TYPES).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className="px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1"
              style={{ 
                backgroundColor: filter === key ? config.color : 'var(--fy-surface-muted)',
                color: filter === key ? 'white' : 'var(--fy-text-secondary)'
              }}
            >
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Items */}
      <main className="max-w-3xl mx-auto px-4 py-4 space-y-3">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--fy-text-disabled)' }} />
            <p className="font-medium" style={{ color: 'var(--fy-text-secondary)' }}>No items yet</p>
            <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>
              {filter === 'all' ? 'Nothing in this notebook yet' : `No ${ITEM_TYPES[filter]?.label.toLowerCase()} items`}
            </p>
          </div>
        ) : (
          filteredItems.map((item) => {
            const typeConfig = ITEM_TYPES[item.type] || ITEM_TYPES.notes;
            const TypeIcon = typeConfig.icon;
            const isNew = !item.viewed && userRole === 'student';
            
            return (
              <Card 
                key={item.item_id}
                className="p-4 relative overflow-hidden"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
              >
                {/* Type indicator bar */}
                <div 
                  className="absolute left-0 top-0 bottom-0 w-1"
                  style={{ backgroundColor: typeConfig.color }}
                />
                
                {isNew && (
                  <div 
                    className="absolute top-2 right-2 w-2 h-2 rounded-full"
                    style={{ backgroundColor: 'var(--lk-secondary-orange)' }}
                  />
                )}
                
                <div className="flex items-start gap-3 ml-2">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${typeConfig.color}20` }}
                  >
                    <TypeIcon className="w-5 h-5" style={{ color: typeConfig.color }} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
                      >
                        {typeConfig.label}
                      </span>
                      {item.requires_acknowledgment && item.acknowledged && (
                        <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'var(--lk-sage-muted)', color: 'var(--lk-primary-green)' }}>
                          <Check className="w-3 h-3" />
                          Acknowledged
                        </span>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                      {item.title}
                    </h3>
                    
                    {item.content && (
                      <p className="text-sm line-clamp-2 mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
                        {item.content}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      {item.from_teacher && (
                        <span>From: {item.from_teacher}</span>
                      )}
                      {item.attachments?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          {item.attachments.length} file{item.attachments.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      {item.requires_acknowledgment && !item.acknowledged && userRole !== 'parent' && (
                        <Button
                          size="sm"
                          onClick={() => acknowledgeItem(item.item_id)}
                          className="rounded-full"
                          style={{ backgroundColor: typeConfig.color }}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                      {item.attachments?.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </main>

      {/* Teacher Quick Add */}
      {userRole === 'teacher' && (
        <div className="fixed bottom-4 right-4">
          <Button
            onClick={() => navigate(`/institutions/${institutionId}/students/${studentId}/notebook/new`)}
            className="h-14 w-14 rounded-full shadow-lg"
            style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
            data-testid="add-notebook-item-btn"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>
      )}
    </div>
  );
}
